import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Container,
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Avatar,
} from '@mui/material';
import {
  Calculate,
  MenuBook,
  Public,
  Science,
  Language,
  Quiz,
  EmojiEvents,
  Assessment,
  Logout,
  School,
  Assignment,
  Edit,
} from '@mui/icons-material';

const areas = [
  { 
    id: 'matematicas', 
    name: 'Matemáticas', 
    icon: Calculate, 
    color: '#e91e63',
    description: 'Álgebra, geometría y cálculo'
  },
  { 
    id: 'lectura-critica', 
    name: 'Lectura Crítica', 
    icon: MenuBook, 
    color: '#9c27b0',
    description: 'Comprensión y análisis de textos'
  },
  { 
    id: 'sociales', 
    name: 'Sociales y Ciudadanas', 
    icon: Public, 
    color: '#3f51b5',
    description: 'Historia, geografía y ciudadanía'
  },
  { 
    id: 'ciencias', 
    name: 'Ciencias', 
    icon: Science, 
    color: '#009688',
    description: 'Física, química y biología'
  },
  { 
    id: 'ingles', 
    name: 'Inglés', 
    icon: Language, 
    color: '#ff5722',
    description: 'Comprensión lectora en inglés'
  },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleAreaClick = (areaId) => {
    navigate(`/area/${areaId}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* AppBar removido - ahora se usa el Navbar global */}

      <Container sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            ¡Bienvenido, {user?.name}!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Elige cómo quieres estudiar hoy
          </Typography>
        </Box>

        {/* Quick Actions */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Panel de Profesores - Solo para teachers y admins */}
          {(user?.role === 'teacher' || user?.role === 'admin') && (
            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  height: '100%', 
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  color: 'white'
                }}
              >
                <CardActionArea onClick={() => navigate('/teacher')} sx={{ height: '100%', p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Edit sx={{ fontSize: 60 }} />
                    <Box>
                      <Typography variant="h5" fontWeight="bold">
                        Panel de Profesores
                      </Typography>
                      <Typography variant="body2">
                        Crear y gestionar preguntas
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          )}
          
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%', 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white'
              }}
            >
              <CardActionArea onClick={() => navigate('/simulacro')} sx={{ height: '100%', p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Quiz sx={{ fontSize: 60 }} />
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      Simulacro Completo
                    </Typography>
                    <Typography variant="body2">
                      Examen completo con todas las áreas
                    </Typography>
                  </Box>
                </Box>
              </CardActionArea>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%', 
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                color: 'white'
              }}
            >
              <CardActionArea onClick={() => navigate('/exams')} sx={{ height: '100%', p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Assignment sx={{ fontSize: 60 }} />
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      Exámenes Específicos
                    </Typography>
                    <Typography variant="body2">
                      Practica con exámenes curados
                    </Typography>
                  </Box>
                </Box>
              </CardActionArea>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%', 
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white'
              }}
            >
              <CardActionArea onClick={() => navigate('/historial')} sx={{ height: '100%', p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Assessment sx={{ fontSize: 60 }} />
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      Mi Historial
                    </Typography>
                    <Typography variant="body2">
                      Revisa tu progreso y resultados
                    </Typography>
                  </Box>
                </Box>
              </CardActionArea>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%', 
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white'
              }}
            >
              <CardActionArea onClick={() => navigate('/ranking')} sx={{ height: '100%', p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <EmojiEvents sx={{ fontSize: 60 }} />
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      Ranking
                    </Typography>
                    <Typography variant="body2">
                      Compite con otros mentores
                    </Typography>
                  </Box>
                </Box>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>

        {/* Study Areas */}
        <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
          Estudiar por Área
        </Typography>
        <Grid container spacing={3}>
          {areas.map((area) => (
            <Grid item xs={12} sm={6} md={4} key={area.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  }
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      p: 2,
                      borderRadius: '50%',
                      bgcolor: area.color,
                      color: 'white',
                      mb: 2,
                    }}
                  >
                    <area.icon sx={{ fontSize: 40 }} />
                  </Box>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    {area.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {area.description}
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    fullWidth
                    sx={{ mt: 1 }}
                    onClick={() => handleAreaClick(area.id)}
                  >
                    Ver Opciones
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Dashboard;
